import type { DealProductBuilderRow, FixedRateLoanDealProductBuilderRow } from '@src/types/OTC/otcProductBuilderTypes';
import { fixedRateLoanFieldDefinitions } from '@src/utils/OTC/fixedRateLoanFieldDefinitions';
import { isUnsecuredLoanSecurityType } from '@src/utils/OTC/loanSecurity';
import { getColumnTitle, getColumnTooltip } from '@src/utils/fieldDefinitionUtils';
import type { useDisclosureWithContext } from '@talos/kyoko';
import { useMemo } from 'react';
import { useDealProductBuilderAssets } from '../DealProductBuilderAssetsProvider';
import { useDealProductBuilder } from '../DealProductBuilderProvider/useDealProductBuilder';
import type { DealProductBuilderColumn } from '../dealProductBuilderColDef';
import { DealProductBuilderGrid } from './DealProductBuilderGrid';
import { buildCurrencySelectorColumn } from './baseProductBuilderColumns';
import { buildLoanSecurityTypeToggleColumn } from './buildLoanSecurityTypeToggleColumn';

interface FixedRateLoanProductBuilderGridProps {
  panelID: string;
  productDetailsDisclosure: ReturnType<
    typeof useDisclosureWithContext<{ rowIndex: number; rowData: DealProductBuilderRow }>
  >;
}

export const FixedRateLoanProductBuilderGrid = ({
  panelID,
  productDetailsDisclosure,
}: FixedRateLoanProductBuilderGridProps) => {
  const { availableBorrowedAssets, availableCollateralAssets } = useDealProductBuilderAssets();
  const {
    state: { rowData },
  } = useDealProductBuilder();
  const loanSecurityType = rowData[0]?.product === 'fixedRateLoan' ? rowData[0].loanSecurityType : undefined;
  const isUnsecured = isUnsecuredLoanSecurityType(loanSecurityType);

  const customColumns = useMemo(() => {
    const borrowedAssetColumn = buildCurrencySelectorColumn<FixedRateLoanDealProductBuilderRow>({
      key: 'baseAsset',
      headerName: getColumnTitle(fixedRateLoanFieldDefinitions.borrowedAsset),
      currenciesToInclude: availableBorrowedAssets,
      description: getColumnTooltip(fixedRateLoanFieldDefinitions.borrowedAsset) ?? '',
    });
    const collateralizationColumn = buildLoanSecurityTypeToggleColumn({
      headerName: getColumnTitle(fixedRateLoanFieldDefinitions.collateralization),
      description: getColumnTooltip(fixedRateLoanFieldDefinitions.collateralization) ?? '',
    });
    // Always include Collateral Asset so AG Grid maintainColumnOrder does not append it after Loan Fields
    // when toggling Unsecured → Secured. hide keeps the header out of the DOM while unsecured.
    const collateralAssetColumn = {
      ...buildCurrencySelectorColumn<FixedRateLoanDealProductBuilderRow>({
        key: 'collateralAsset',
        headerName: getColumnTitle(fixedRateLoanFieldDefinitions.collateralAsset),
        currenciesToInclude: availableCollateralAssets,
        description: getColumnTooltip(fixedRateLoanFieldDefinitions.collateralAsset) ?? '',
      }),
      hide: isUnsecured,
    };
    return [borrowedAssetColumn, collateralizationColumn, collateralAssetColumn] as DealProductBuilderColumn[];
  }, [availableBorrowedAssets, availableCollateralAssets, isUnsecured]);

  return (
    <DealProductBuilderGrid
      panelID={panelID}
      productDetailsDisclosure={productDetailsDisclosure}
      customColumnDefs={customColumns}
      fieldDefinitions={fixedRateLoanFieldDefinitions}
    />
  );
};
